import React, { useState, useEffect, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import Utils from 'constants/config'
import LocalStorage from 'utils/myLocalStorage'
import ComponentsUNames from 'constants/componentsUNames'
import {
  Container,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@airbus/components-react/components'
import UrlApis from 'utils/api/urlApis'
import useQuery from 'utils/hooks/useQuery'
import { useNavigate, useSearchParams } from 'react-router-dom'
import LangContext from 'context/LangContext'
import PageHeader from 'components/pageHeader'
import { Order, PaginatedOrder } from 'types/order'
import { NavItem } from 'types/navItems'
import ResponsiveTableCell from 'components/responsiveTableCell'

const OrdersPage = () => {
  const baseUrl = process.env.PUBLIC_URL
  const { t } = useTranslation()

  const [searchParams] = useSearchParams()
  const jumpToPage = Number(searchParams.get('page')) || 0

  const navItems: NavItem[] = [
    {
      title: t('9420008'),
      url: baseUrl + '/orders',
    },
  ]

  const [orders, setOrders] = useState<Order[]>()
  const { fetchData: fetchOrders } = useQuery<PaginatedOrder>(UrlApis.URL_ORDERS_GET)

  const navigate = useNavigate()
  const { lang } = useContext(LangContext)

  const [pagination, setPagination] = useState({
    nbItems: 0,
    pageSize: LocalStorage.getOrDefault(
      [
        LocalStorage.KEY_PREFS,
        ComponentsUNames.ORDERS_STORE_NAME,
        ComponentsUNames.ORDERS_TABLE_UNAME + '|' + ComponentsUNames.PROP_PAGE_SIZE,
      ],
      LocalStorage.VALUE_TYPE.INTEGER,
      Utils.PAGINATION_DEFAULT_PAGESIZE,
    ),
    pageNo: 0,
    nbPages: 0,
    choiceNbRows: [10, 20, 30, 40, 50],
  })

  const canViewPrice = LocalStorage.getOrDefault(
    [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.GLOBAL_PRICE_DSP_PERMISSIONS],
    LocalStorage.VALUE_TYPE.BOOLEAN,
    false,
  )

  const handlePageChange = (page: number) => {
    navigate(baseUrl + '/orders?page=' + encodeURIComponent(page), {
      preventScrollReset: true,
    })
  }

  useEffect(() => {
    ;(async () => {
      const ordersResponse = await fetchOrders({
        lang,
        size: pagination.pageSize,
        page: jumpToPage,
      })
      setOrders(ordersResponse?.data?.content)
      setPagination({
        ...pagination,
        nbItems: ordersResponse?.data.totalElements,
        nbPages: ordersResponse?.data.totalPages,
        pageNo: ordersResponse?.data.number,
      })
    })()
  }, [lang, pagination.pageSize, jumpToPage])

  return (
    <>
      <PageHeader pageTitle={t('9420008')} navItems={navItems} />
      <Container style={{ flex: 1 }} until='xl'>
        <Stack>
          {orders && JSON.stringify(orders) !== '{}' && JSON.stringify(orders) !== '[]' ? (
            <div>
              <Table>
                <col style={{ width: '10%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '7%' }} />
                <TableHead>
                  <TableRow>
                    <ResponsiveTableCell>{t('1P00012')}</ResponsiveTableCell>
                    <ResponsiveTableCell>{t('9500121')}</ResponsiveTableCell>
                    <ResponsiveTableCell>{t('9500034')}</ResponsiveTableCell>
                    <ResponsiveTableCell>{t('7000480')}</ResponsiveTableCell>
                    <ResponsiveTableCell>{t('7000588')}</ResponsiveTableCell>
                    <ResponsiveTableCell>{t('7000892')}</ResponsiveTableCell>
                    <ResponsiveTableCell>{t('7001518')}</ResponsiveTableCell>
                    <ResponsiveTableCell>{t('7001950')}</ResponsiveTableCell>
                    {canViewPrice && <ResponsiveTableCell align='right'>{t('7001202')}</ResponsiveTableCell>}
                    <ResponsiveTableCell align='right'>{t('9500122')}</ResponsiveTableCell>
                    <ResponsiveTableCell align='right'>{t('9500123')}</ResponsiveTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders &&
                    orders.map((d, i) => (
                      <TableRow
                        id={d.numOrder}
                        key={'line_orders_' + i}
                        onClick={() => navigate(d.numOrder + '?page=' + jumpToPage)}
                        style={{ cursor: 'pointer' }}
                      >
                        <ResponsiveTableCell>{d.type}</ResponsiveTableCell>
                        <ResponsiveTableCell
                          style={{
                            whiteSpace: 'pre',
                            overflow: 'visible',
                            textOverflow: 'unset',
                            wordBreak: 'break-word',
                          }}
                        >
                          {d.numOrder}
                        </ResponsiveTableCell>
                        <ResponsiveTableCell>{d.date}</ResponsiveTableCell>
                        <ResponsiveTableCell>{d.urgence}</ResponsiveTableCell>
                        <ResponsiveTableCell>{d.dateLimite}</ResponsiveTableCell>
                        <ResponsiveTableCell style={{ color: d.validatedByResp ? 'green' : 'inherit' }}>
                          {d.responsable}
                        </ResponsiveTableCell>
                        <ResponsiveTableCell>{d.valideur}</ResponsiveTableCell>
                        <ResponsiveTableCell>{d.fournisseur}</ResponsiveTableCell>
                        {canViewPrice && <ResponsiveTableCell align='right'>{d.totalHt}</ResponsiveTableCell>}
                        <ResponsiveTableCell align='right'>{d.nivRequis}</ResponsiveTableCell>
                        <ResponsiveTableCell align='right'>{d.nivVal}</ResponsiveTableCell>
                      </TableRow>
                    ))}
                </TableBody>

                <TableRow>
                  <ResponsiveTableCell colSpan='11'>
                    <Pagination
                      count={pagination.nbItems}
                      page={pagination.pageNo + 1}
                      onPageChange={(e, newPage) => handlePageChange(newPage - 1)}
                      rowsPerPage={pagination.pageSize}
                      onRowsPerPageChange={(e, newRowPerPage) => {
                        LocalStorage.put(
                          [
                            LocalStorage.KEY_PREFS,
                            ComponentsUNames.ORDERS_TABLE_UNAME,
                            ComponentsUNames.ORDERS_TABLE_UNAME + '|PAGESIZE',
                          ],
                          newRowPerPage,
                        )
                        setPagination({ ...pagination, pageSize: parseInt(newRowPerPage), pageNo: 0 })
                      }}
                      rowsPerPageOptions={pagination.choiceNbRows}
                      showRowsPerPage
                      rowsPerPageLabel={t('entries_per_page')}
                    />
                  </ResponsiveTableCell>
                </TableRow>
              </Table>
            </div>
          ) : (
            <Typography style={{ textAlign: 'center' }}>{t('9500055')}</Typography>
          )}
        </Stack>
      </Container>
    </>
  )
}

export default OrdersPage
